import { useEffect, useState } from 'react';
let cards = {};
const bySource = {};
const listeners = new Set();
/**
 * Parses an ear's `kamo_si` property; null when it is not one.
 *
 * @param {string} raw - The property's value.
 * @returns {ISiCard|null}
 */
export function parseCard(raw) {
    if (typeof raw !== 'string' || !raw || raw.length > 4000) {
        return null;
    }
    let v;
    try {
        v = JSON.parse(raw);
    }
    catch (e) {
        return null;
    }
    if (!v || typeof v !== 'object' || typeof v.si !== 'string' || !/^[0-9a-zA-Z_-]{1,64}$/.test(v.si)) {
        return null;
    }
    const av = typeof v.av === 'string' && v.av.startsWith('https://') && v.av.length < 2000 ? v.av : '';
    const listening = Array.isArray(v.l)
        ? v.l.filter((x) => typeof x === 'string' && /^[0-9a-zA-Z_-]{1,64}$/.test(x)).slice(0, 12)
        : [];
    return {
        si: v.si,
        memberId: typeof v.m === 'string' && /^\d{1,20}$/.test(v.m) ? v.m : '',
        avatarUrl: av,
        listening
    };
}
/**
 * An ear's card arrived or changed (or went: null).
 *
 * @param {string} earId - The ear participant it came from.
 * @param {ISiCard|null} card - The card.
 * @returns {void}
 */
export function setCard(earId, card) {
    const next = { ...cards };
    const was = bySource[earId];
    if (was && (!card || was !== card.si)) {
        delete next[was];
        delete bySource[earId];
    }
    if (card) {
        next[card.si] = card;
        bySource[earId] = card.si;
    }
    cards = next;
    listeners.forEach(l => l());
}
/**
 * Every card, by the SI's tile.
 *
 * @returns {Object}
 */
export function allCards() {
    return cards;
}
/**
 * The card of the SI whose tile this is, if any.
 *
 * @param {string} participantId - A tile's participant.
 * @returns {ISiCard|undefined}
 */
export function cardOf(participantId) {
    return cards[participantId];
}
/**
 * Subscribes to changes.
 *
 * @param {Function} l - The listener.
 * @returns {Function}
 */
export function subscribe(l) {
    listeners.add(l);
    return () => {
        listeners.delete(l);
    };
}
/**
 * The card of the SI on this tile, live.
 *
 * @param {string} participantId - A tile's participant.
 * @returns {ISiCard|undefined}
 */
export function useSiCard(participantId) {
    const [card, setOne] = useState(() => cards[participantId]);
    useEffect(() => {
        setOne(cards[participantId]);
        return subscribe(() => setOne(cards[participantId]));
    }, [participantId]);
    return card;
}
/**
 * Tests only.
 *
 * @returns {void}
 */
export function _reset() {
    cards = {};
    Object.keys(bySource).forEach(k => delete bySource[k]);
}
